import React from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Tag,
  Text,
  VStack,
  Select,
} from "@chakra-ui/react";
import { SelectedDeliveryOption } from "../../types";
import { ValidationError } from "../../validation/deliverySelectionValidation";
import { QuickEtaOption } from "../../hooks/useManualOptionHandlers";
import { CPTSlabForManualOption } from "../../../../views/Orders/enums";
import { CPTSlabSelector } from "./CPTSlabSelector";
import { useGetOrderMediaConfig } from "../../../../views/PlaceOrderTest/queryHooks";
import {
  DELETE_COLUMN_WIDTH,
  OPTION_ROW_OUTER_GAP,
  getDeliveryOptionCardStyles,
} from "../constants/deliveryOptionRowLayout";
import {
  filterSupplierOptionsForManualCptSlab,
  resolveManualFulfillmentSupplierForCptSlab,
} from "../../utils/resolveManualFulfillmentSupplierForCptSlab";

export interface ManualDeliveryOptionFormProps {
  isManualSelected: boolean;
  isExpired: boolean;
  isRemainingItems: boolean;
  /** Config-gated disable; control stays visible. */
  isConfigDisabled?: boolean;
  configDisabledTooltip?: string;
  selection: SelectedDeliveryOption | undefined;
  quickEtaOptions: QuickEtaOption[];
  errorsByField: Record<string, ValidationError[] | undefined>;
  manualSaveError: string | null;
  isSavingManualOption: boolean;
  onManualOptionSelect: () => void;
  onManualCPTSlabChange: (val: CPTSlabForManualOption) => void;
  onManualCPTChange: (value: string) => void;
  onManualChargeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuickEtaSelect: (minutes: number) => void;
  onCancel: () => void;
  onSave: () => void;
  supplierOptions?: Array<{
    supplierId: string;
    businessName: string;
    branchName?: string | null;
    branchCode?: string | null;
    isAssociated: boolean;
    address?: any;
  }>;
  onManualSupplierChange?: (supplierId: string) => void;
  transactionType?: string;
  allowTradingSelfPickup?: boolean;
  isManualFulfillmentRoutingRestricted?: boolean;
}

export const ManualDeliveryOptionForm: React.FC<
  ManualDeliveryOptionFormProps
> = ({
  isManualSelected,
  isExpired,
  isRemainingItems,
  isConfigDisabled = false,
  configDisabledTooltip = "Adding Manual Delivery Option is restricted",
  selection,
  quickEtaOptions,
  errorsByField,
  manualSaveError,
  isSavingManualOption,
  onManualOptionSelect,
  onManualCPTSlabChange,
  onManualCPTChange,
  onManualChargeChange,
  onQuickEtaSelect,
  onCancel,
  onSave,
  supplierOptions,
  onManualSupplierChange,
  transactionType,
  allowTradingSelfPickup = false,
  isManualFulfillmentRoutingRestricted = false,
}) => {
  const isInteractionDisabled =
    isExpired || isRemainingItems || isConfigDisabled;
  const cardStyles = getDeliveryOptionCardStyles({
    isSelected: isManualSelected && !isConfigDisabled,
    isInteractionDisabled,
  });

  const { data: configData } = useGetOrderMediaConfig();

  const isSupplierSelectionDisabled = React.useMemo(() => {
    return configData?.ff_disable_manual_delivery_supplier_selection !== "false";
  }, [configData?.ff_disable_manual_delivery_supplier_selection]);

  const visibleSupplierOptions = React.useMemo(
    () =>
      filterSupplierOptionsForManualCptSlab(
        selection?.deliveryDetails?.manualEntry?.cptSlab,
        supplierOptions,
        isManualFulfillmentRoutingRestricted,
      ),
    [
      selection?.deliveryDetails?.manualEntry?.cptSlab,
      supplierOptions,
      isManualFulfillmentRoutingRestricted,
    ],
  );

  const defaultSupplierId = React.useMemo(() => {
    if (
      isManualFulfillmentRoutingRestricted &&
      selection?.deliveryDetails?.manualEntry?.cptSlab != null
    ) {
      return (
        resolveManualFulfillmentSupplierForCptSlab(
          selection.deliveryDetails.manualEntry.cptSlab,
          supplierOptions,
        ) ||
        visibleSupplierOptions?.[0]?.supplierId ||
        ""
      );
    }
    const splitSupplier = visibleSupplierOptions?.find(
      (opt) => !opt.isAssociated,
    );
    if (splitSupplier) return splitSupplier.supplierId;
    return visibleSupplierOptions?.[0]?.supplierId || "";
  }, [
    visibleSupplierOptions,
    isManualFulfillmentRoutingRestricted,
    selection?.deliveryDetails?.manualEntry?.cptSlab,
    supplierOptions,
  ]);

  const selectedSupplierId =
    selection?.deliveryDetails?.manualEntry?.supplierId || defaultSupplierId;

  const isFulfillmentSupplierLocked =
    isManualFulfillmentRoutingRestricted &&
    selection?.deliveryDetails?.manualEntry?.cptSlab != null;

  return (
    <HStack spacing={OPTION_ROW_OUTER_GAP} align="center" w="100%">
      <Box
        flex={1}
        {...cardStyles}
        onClick={() => !isInteractionDisabled && onManualOptionSelect()}
      >
        <Flex justify="space-between" align="center" w="100%">
          <HStack spacing={3} align="center">
            <Text
              fontSize="0.75rem"
              fontWeight="700"
              textTransform="uppercase"
              color={isConfigDisabled ? "gray.400" : "purple.600"}
              fontFamily="'Nunito','Inter', sans-serif"
              letterSpacing="wider"
              opacity={isConfigDisabled ? 0.7 : 1}
              cursor={isConfigDisabled ? "not-allowed" : "pointer"}
              title={isConfigDisabled ? configDisabledTooltip : undefined}
            >
              + ADD OPTION
            </Text>
          </HStack>

          {isManualSelected && !isConfigDisabled && (
            <HStack spacing={3} align="center">
              <CPTSlabSelector
                value={selection?.deliveryDetails?.manualEntry?.cptSlab}
                onChange={onManualCPTSlabChange}
                isDisabled={isInteractionDisabled}
                transactionType={transactionType}
                allowTradingSelfPickup={allowTradingSelfPickup}
              />

              {visibleSupplierOptions && visibleSupplierOptions.length > 0 && (
                <HStack spacing={1.5} align="center">
                  <Select
                    size="xs"
                    variant="outline"
                    bg="white"
                    fontSize="12px"
                    fontWeight="600"
                    height="24px"
                    borderRadius="md"
                    value={selectedSupplierId}
                    onChange={(e) => onManualSupplierChange?.(e.target.value)}
                    isDisabled={
                      isSupplierSelectionDisabled || isInteractionDisabled
                    }
                    pointerEvents={
                      isFulfillmentSupplierLocked ? "none" : undefined
                    }
                    onMouseDown={
                      isFulfillmentSupplierLocked
                        ? (e) => e.preventDefault()
                        : undefined
                    }
                    icon={isFulfillmentSupplierLocked ? <></> : undefined}
                    _hover={{ borderColor: "gray.300" }}
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                    }}
                    sx={
                      isFulfillmentSupplierLocked
                        ? {
                            paddingInlineEnd: "8px",
                            ".chakra-select__icon-wrapper": { display: "none" },
                          }
                        : undefined
                    }
                    w="170px"
                  >
                    {visibleSupplierOptions.map((opt) => (
                      <option key={opt.supplierId} value={opt.supplierId}>
                        {opt.businessName} {opt.branchName ? `(${opt.branchName})` : ""} {opt.isAssociated ? "- Associated Branch" : ""}
                      </option>
                    ))}
                  </Select>
                </HStack>
              )}
            </HStack>
          )}
        </Flex>

        {isManualSelected && !isConfigDisabled && (
          <VStack
            align="stretch"
            spacing={3}
            mt={2}
            onClick={(e) => e.stopPropagation()}
          >
            <Box>
              <Text
                fontSize="0.7rem"
                color="gray.500"
                fontWeight="700"
                mb={2}
              >
                QUICK SELECT CPT
              </Text>
              <Flex wrap="wrap" gap={2}>
                {quickEtaOptions.map((opt) => (
                  <Tag
                    key={opt.label}
                    size="sm"
                    variant="subtle"
                    colorScheme="blue"
                    cursor={isInteractionDisabled ? "not-allowed" : "pointer"}
                    onClick={() =>
                      !isInteractionDisabled && onQuickEtaSelect(opt.minutes)
                    }
                    opacity={isInteractionDisabled ? 0.6 : 1}
                    _hover={{
                      bg: isInteractionDisabled ? undefined : "blue.100",
                    }}
                    fontFamily="'Nunito','Inter', sans-serif"
                  >
                    {opt.label}
                  </Tag>
                ))}
              </Flex>
            </Box>

            <HStack spacing={4} align="start">
              <Input
                type="datetime-local"
                size="sm"
                fontSize="0.85rem"
                fontWeight="500"
                placeholder="Select CPT"
                borderColor={
                  selection?.deliveryDetails?.manualEntry?.cpt
                    ? "gray.300"
                    : "red.300"
                }
                _focus={{
                  borderColor: "blue.400",
                  boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                }}
                value={selection?.deliveryDetails?.manualEntry?.cpt || ""}
                onChange={(e) => onManualCPTChange(e.target.value)}
                isDisabled={isInteractionDisabled}
                flex="1"
              />
              <InputGroup w="140px" size="sm">
                <InputLeftElement
                  pointerEvents="none"
                  fontSize="0.8rem"
                  color="gray.500"
                >
                  ₹
                </InputLeftElement>
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="0"
                  fontSize="0.85rem"
                  fontWeight="600"
                  textAlign="right"
                  value={
                    selection?.deliveryDetails?.manualEntry?.deliveryCharge ??
                    ""
                  }
                  onChange={onManualChargeChange}
                  isDisabled={isInteractionDisabled}
                  borderColor={
                    selection?.deliveryDetails?.manualEntry?.deliveryCharge !==
                    undefined
                      ? "gray.300"
                      : "red.300"
                  }
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                  }}
                />
              </InputGroup>
            </HStack>
            {errorsByField.deliveryCharge?.map((error: ValidationError) => (
              <Text
                key={error.id}
                fontSize="12px"
                fontWeight="semibold"
                color="red.500"
                mt={1}
                ml={1}
              >
                *{error.message}
              </Text>
            ))}
            {errorsByField.general?.map((error: ValidationError) => (
              <Text
                key={error.id}
                fontSize="12px"
                fontWeight="semibold"
                color="red.500"
                mt={1}
                ml={1}
              >
                *{error.message}
              </Text>
            ))}

            <HStack justify="flex-end" spacing={2} pt={1}>
              <Button
                size="sm"
                variant="outline"
                borderRadius="full"
                onClick={onCancel}
                isDisabled={isInteractionDisabled}
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="700"
              >
                CANCEL
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                borderRadius="full"
                onClick={onSave}
                isLoading={isSavingManualOption}
                isDisabled={
                  isInteractionDisabled ||
                  !selection?.deliveryDetails?.manualEntry?.cpt ||
                  !selection?.deliveryDetails?.manualEntry?.cptSlab ||
                  (errorsByField.general?.length ?? 0) > 0
                }
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="800"
              >
                SAVE
              </Button>
            </HStack>
            {manualSaveError && (
              <Text
                fontSize="12px"
                fontWeight="semibold"
                color="red.500"
                mt={1}
                ml={1}
              >
                *{manualSaveError}
              </Text>
            )}
          </VStack>
        )}
      </Box>
      {!isRemainingItems && <Box w={DELETE_COLUMN_WIDTH} flexShrink={0} />}
    </HStack>
  );
};
